/**
 * Created by Ning Jiang on 12/7/2016.
 */

import { TimelineMax, TweenLite, Bounce, Power2, TimelineLite, Ease } from "gsap";
import { BLEND_MODES, Texture } from "pixi.js";
import { Helper } from "@nolimitcity/slot-game/bin/core/utils/Helper";

import { PunkSymbolLayerType, PunkSymbolLayer } from "./PunkSymbolLayer";
import { Sequence } from "@nolimitcity/slot-game/bin/core/displayobject/Sequence";
import { MathHelper } from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import { ReelSymbolSize, PunkSymbolData } from "./config/PunkSymbolData";
import { ReelSpriteSymbol } from "@nolimitcity/slot-game/bin/core/engines/reel/symbol/ReelSpriteSymbol";
import { ReelSymbolStates } from "@nolimitcity/slot-game/bin/core/engines/reel/symbol/ReelSpriteSymbolArea";
import { ISymbolAnimationConf } from "@nolimitcity/slot-game/bin/core/engines/reel/symbol/config/IReelSymbolData";
import { PunkRocker } from "../PunkRocker";

export class PunkSymbol extends ReelSpriteSymbol {
    
    protected _overlayAnimations:PunkSymbolLayer;

    constructor(ID:number, symName:string, state:string = ReelSymbolStates.NORMAL, size:number = ReelSymbolSize.FOUR) {
        super(ID, symName, state, size);
    }

    public get symbolSizeY():number{
        return [90,108,135,180,270,324,360,450][this._symbolTypeState.state-1];
    }

    public get isNormalSym():boolean{
        return super.isNormalSym || this.symName === "M3_WV";
    }

    protected addColorGraphic():void{
        if(this.symName[0] == "L"){
            this._bgGFX.clear();
            this._bgGFX.blendMode = BLEND_MODES.OVERLAY;
            let color:number = 0x000000;
            switch(parseInt(this.symName[1],16)){
                case 1:
                    color = 0xec003a;
                    break;
                case 2:
                    color = 0xff9b00;
                    break;
                case 3:
                    color = 0x00d701;
                    break;
                case 4:
                    color = 0x30687b;
                    break;
                case 5:
                    color = 0xa8892b;
                    break;
            }
            this._bgGFX.alpha = 0.2;
            this._bgGFX.beginFill( color, 1);
            this._bgGFX.drawRect(-this.symbolWidht*0.5,-this.symbolHeight*0.5,this.symbolWidht, this.symbolHeight);
            this._bgGFX.height = this.symbolHeight;
            this._bgGFX.endFill();
        }else{
            // this._bgGFX.clear();
            this._bgGFX.alpha = 0;
        }
    }

    // +PunkRocker+ //
    protected playAttention(config:ISymbolAnimationConf):TimelineMax{
        const TEXTURES:Texture[] | undefined = PunkSymbolData.getTextureFromConfig(this._symConfig, this.state.state, this._symbolTypeState.state.toString());
        this._timeLine = Helper.timeLineKiller(this._timeLine!, new TimelineMax({
            onComplete: () => this._currentAnimation!.setTexture(TEXTURES!),
            repeat: config.repeat,
            repeatDelay: 0,
        }));

        let TEXTURES2:Texture[] | undefined = PunkSymbolData.getTextureFromConfig(this._symConfig, config.state,  this._symbolTypeState.state.toString());
        if(TEXTURES2 == undefined) TEXTURES2 = TEXTURES;

        this._currentAnimation!.setTexture(TEXTURES2!);
        const duration:number = config.duration == undefined ? this._currentAnimation!.totalDuration : config.duration;
        this._timeLine!.add(this._currentAnimation!.playAnimation({duration:duration}));

        if((this.isWild || this.isScatter)){
            const spinType:string = this.isScatter ? PunkSymbolLayerType.SPARK : PunkSymbolLayerType.SPIN;
            TweenLite.to(this._overlayAnimations.fetchSequence(spinType),0.2,{alpha:0, ease:Power2.easeOut}).eventCallback("onComplete",() => {
                this._overlayAnimations.fetchSequence(spinType).reset(false);
                this._overlayAnimations.fetchSequence(spinType).alpha = 0;
            })
        }
        return this._timeLine;
    }

    protected textureAnimation(config:ISymbolAnimationConf):void{
        const toSize:number = config.type && this._symbolTypeState.state != config.type ? config.type! : this._symbolTypeState.state;

        if(this.isLowValue && config.state == ReelSymbolStates.SPIN && toSize == ReelSymbolSize.FOUR){
            this._currentAnimation.scale.set(1,2);
        }else
            this._currentAnimation.scale.set(1,1);

        if((this.isWild || this.isScatter) && this.state.equal(ReelSymbolStates.SPIN) &&  config.state == ReelSymbolStates.NORMAL ){
            const spinType:string = this.isScatter ? PunkSymbolLayerType.SPARK : PunkSymbolLayerType.SPIN;
            TweenLite.to(this._overlayAnimations.fetchSequence(spinType),0.2,{alpha:0, ease:Power2.easeOut}).eventCallback("onComplete",() => {
                this._overlayAnimations.fetchSequence(spinType).reset(false);
                this._overlayAnimations.fetchSequence(spinType).alpha = 0;
            })
        }
        super.textureAnimation(config);
    }

    public changeSymbol(symName:string):void{
        super.changeSymbol(symName);

        this.addColorGraphic();

        if(this.isLowValue && this.state.equal(ReelSymbolStates.SPIN) && this._symbolTypeState.state == ReelSymbolSize.FOUR){
            this._currentAnimation.scale.set(1,2);
        }else{
            if(!!PunkRocker.api.gameClientConfiguration.useCensoredGfxUS && symName === "M3"){
                symName = "M3_WV";
                this.changeSymbol(symName);
            }
            this._currentAnimation.scale.set(1,1);
        }

        if(!this.isNormalSym){
            this.createLayerOverlay();
        }else{
            if(this._overlayAnimations) this._overlayAnimations.remove();
        }
    }

    public setActive(value:boolean, delay:number = 0, intensity:number = 0.3):TimelineLite{
        if(value && this.isWild && this._overlayAnimations){
            this._overlayAnimations.fetchSequence(PunkSymbolLayerType.IDLE).visible = value;
        }
        if(!this.isNormalSym) return new TimelineLite();
        return super.setActive(value,delay,intensity);
    }

    // +PunkRocker+ //
    public playBg(duration:number = 0.25, toAlpha:number = 0.2):TimelineMax{
        let tl:TimelineMax = new TimelineMax();
        if(this.symName[0] == "L" && this.currentStateAnim!.visible){
            this._bgGFX.clear();
            this._bgGFX.blendMode = BLEND_MODES.ADD;
            if(this._bgGFX.alpha > 0) duration = 0;
            
            let color:number = 0x000000;
            switch(parseInt(this.symName[1],16)){
                case 1:
                    color = 0xec003a;
                    break;
                case 2:
                    color = 0xff9b00;
                    break;
                case 3:
                    color = 0x00d701;
                    break;
                case 4:
                    color = 0x30687b;
                    break;
                case 5:
                    color = 0xa8892b;
                    break;
            }
            this._bgGFX.beginFill( color, 1);
            this._bgGFX.drawRect(-this.symbolWidht*0.5,-this.symbolHeight*0.5,this.symbolWidht, this.symbolHeight);
            this._bgGFX.height = this.symbolHeight;
            this._bgGFX.endFill();

            tl.fromTo(this._bgGFX,duration,{alpha:0}, {alpha: toAlpha});
            tl.play();
        }
        return tl;
    }

    public changeSizeBg(duration:number = 0.25):TimelineMax{
        let tl:TimelineMax = new TimelineMax();
        const symArr:number[] = [90,108,135,180,270,324,360,450];
        if(this._bgGFX.alpha > 0){
            const startHeight:number = symArr[this.stateType.state];
            const nextHeight:number = symArr[this.stateType.state-1];
            tl.fromTo(this._attentionGFX, duration, { height: startHeight }, { height:nextHeight, ease:Bounce.easeOut },0);
            tl.fromTo(this._bgGFX, duration, { height: startHeight }, { height:nextHeight, ease:Bounce.easeOut },0);
            tl.play();
        }
        return tl;
    }

    protected playTextureAnimation(TextureFrom:Texture[] | undefined,TextureTo:Texture[] | undefined,duration:number,_ease?:any):void{
        if(TextureTo){
            this._currentAnimation!.setTexture(TextureTo);
            this._overlaySprite.texture = this.currentStateAnim!.texture;

            this._timeLine!.fromTo(this._currentAnimation, duration,{alpha:0}, {alpha:1, ease : _ease},0)

            if(TextureFrom){
                this._oldAnimation!.setTexture(TextureFrom);
                this._timeLine!.fromTo(this._oldAnimation , duration,{alpha:1}, {alpha:0, ease : _ease},0);
            }
        }
    }

    // +PunkRocker+ //
    public setSuperVisibility(value:boolean):void{
        super.setSuperVisibility(value);
        if(this._overlayAnimations) this._overlayAnimations.fetchSequence(PunkSymbolLayerType.IDLE).visible = value;
    }
    
    protected createLayerOverlay():void{
        if(this._overlayAnimations === undefined) this._overlayAnimations = new PunkSymbolLayer(this);
        if(this.isMystery) return;

        if(this.isWild){
            const idleAnimaton:Sequence = this._overlayAnimations.fetchSequence(PunkSymbolLayerType.IDLE);
            idleAnimaton.playAnimation({
                onStart:() => idleAnimaton.alpha = 1,
                onUpdate:() => {
                idleAnimaton.visible = this.posID >= 0;
                if(idleAnimaton.visible) idleAnimaton.position = MathHelper.pointSub(Helper.copyLocalPosition(idleAnimaton,this),<PIXI.Point>idleAnimaton.parent.position);
            },
            repeat:-1});
        }

        if(this.state.equal(ReelSymbolStates.SPIN)){
            const spinType:string = this.isScatter ? PunkSymbolLayerType.SPARK : PunkSymbolLayerType.SPIN;
            const spinAnimaton:Sequence = this._overlayAnimations.fetchSequence(spinType);
            spinAnimaton.playAnimation({
                onStart:() => spinAnimaton.alpha = 1,
                onUpdate:() => {
                spinAnimaton.position = MathHelper.pointSub(Helper.copyLocalPosition(spinAnimaton,this),<PIXI.Point>spinAnimaton.parent.position);
            },
            repeat:-1});
        }
    }

    public setVisibleForSymbol(value:boolean):void{
        if(this._oldAnimation != undefined) this._oldAnimation.visible = value;
        if(this.currentStateAnim != undefined){
            this.currentStateAnim.visible = value;
            this.currentStateAnim.alpha = value ? 1 : 0;
        } 
    }

    public attentionDarkness(alphaValue:number = 0.6, duration:number = 0.2):TimelineMax   {
        this._subTimeline = Helper.timeLineKiller(this._subTimeline!, new TimelineMax());
        if(!this.isNormalSym) return this._subTimeline;

        const startAlpha = this._attentionGFX.alpha;
        const toAlpha = alphaValue;

        if(this._attentionGFX.height == 0){
            this._attentionGFX.beginFill( 0x000000, 1);
            this._attentionGFX.drawRect(-this.symbolWidht*0.5,-this.symbolHeight*0.5,this.symbolWidht, this.symbolHeight);
            this._attentionGFX.height = this.symbolHeight;
            this._attentionGFX.endFill();
        }
        this._subTimeline.fromTo(this._attentionGFX , duration, {alpha: startAlpha }, {alpha: toAlpha });
        return this._subTimeline;
    }

    protected ChangeVisibility(config:ISymbolAnimationConf):void{
        let alphaFrom:number = config.state == ReelSymbolStates.HIDE ?  1 : 0;
        let alphaTo:number = config.state == ReelSymbolStates.HIDE ?  0 : 1;

        this._timeLine!.add([
            TweenLite.fromTo(this , config.duration ? config.duration : 0,
                {alpha: alphaFrom }, {alpha:alphaTo, ease : config.ease}),
        ]);
    }

    public remove():void{
        if(this._overlayAnimations) this._overlayAnimations.remove();
        super.remove();
    }
}
